import { GeometricPlanRequestModel } from '@app/ps/models/geometric-plan-request.model';
import { Inject, Injectable } from '@angular/core';
import { NoteModel } from '@app/common/models/note.model';
import { geometricPlanRequestTypeOptions } from '@app/ps/enums/geometric-plan-request-type.enum';
import { geometricPlanRequestStatusOptions } from '@app/ps/enums/geometric-plan-request-status.enum';
import { approveOptions } from '@app/ps/enums/approve.enum';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { AttachmentTypeModel } from '@app/common/models/attachment-type.model';
import { AuthService } from '@app/common/services/auth.service';
import { Restangular } from 'ngx-restangular';
import { UploadService } from '@app/common/services/upload.service';
import { APP_BRAND } from '@app/common/services/config.service';
import { ErrorHandlerService } from '@app/common/services/error-handler.service';
import { FileUtils } from '@app/common/utils/file.utils';

@Injectable({
  providedIn: 'root'
})
export class GeometricPlanRequestService {
  constructor(
    private uploadService: UploadService,
    private restangular: Restangular,
    private authService: AuthService,
    private errorHandlerService: ErrorHandlerService,
    @Inject(APP_BRAND) public APP_BRAND: any,
  ) {
  }

  create(geometricPlanRequest: GeometricPlanRequestModel, notes: NoteModel[] = []): Promise<GeometricPlanRequestModel> {
    let createdGeometricPlanRequest: GeometricPlanRequestModel;
    return this.restangular
      .all('geometric-plan-requests/create')
      .customPOST(
        geometricPlanRequest,
        '',
        { loadCollections: [ 'areas', 'constructionObjects', 'parcels', 'folder' ] }
      )
      .toPromise()
      .then(response => {
        createdGeometricPlanRequest = response.plain();
        return this.createNotes(createdGeometricPlanRequest, notes);
      })
      .then(() => createdGeometricPlanRequest);
  }

  createFromExisting(
    geometricPlanRequest: GeometricPlanRequestModel,
    gpFiles: UploadFileExtended[] = [],
    gpVfkFiles: UploadFileExtended[] = [],
    gpDocFiles: UploadFileExtended[] = [],
  ): Promise<GeometricPlanRequestModel> {
    let createdGeometricPlanRequest: GeometricPlanRequestModel;
    return this.restangular
      .all('geometric-plan-requests/create-from-existing')
      .customPOST(
        geometricPlanRequest,
        '',
        { loadCollections: [ 'areas' ] }
      )
      .toPromise()
      .then(response => {
        createdGeometricPlanRequest = response.plain();
      })
      .then(() => this.createDocuments(createdGeometricPlanRequest, gpFiles, 'GEOMETRIC_PLAN'))
      .then(() => this.createDocuments(createdGeometricPlanRequest, gpVfkFiles, 'GEOMETRIC_PLAN_VFK'))
      .then(() => this.createDocuments(createdGeometricPlanRequest, gpDocFiles, 'GEOMETRIC_PLAN_DOCUMENTATION'))
      .then(() => createdGeometricPlanRequest);
  }

  createFromPrepared(
    geometricPlanRequest: GeometricPlanRequestModel,
    notes: NoteModel[] = [],
    gpFiles: UploadFileExtended[] = [],
    gpVfkFiles: UploadFileExtended[] = [],
    gpDocFiles: UploadFileExtended[] = [],
  ): Promise<GeometricPlanRequestModel> {
    let createdGeometricPlanRequest: GeometricPlanRequestModel;
    return this.restangular
      .all('geometric-plan-requests/create-from-prepared')
      .customPOST(
        geometricPlanRequest,
        '',
        { loadCollections: [ 'areas' ] }
      )
      .toPromise()
      .then(response => {
        createdGeometricPlanRequest = response.plain();
      })
      .then(() => this.createNotes(createdGeometricPlanRequest, notes)
      .then(() => this.createDocuments(createdGeometricPlanRequest, gpFiles, 'GEOMETRIC_PLAN'))
      .then(() => this.createDocuments(createdGeometricPlanRequest, gpVfkFiles, 'GEOMETRIC_PLAN_VFK'))
      .then(() => this.createDocuments(createdGeometricPlanRequest, gpDocFiles, 'GEOMETRIC_PLAN_DOCUMENTATION'))
      .then(() => createdGeometricPlanRequest));
  }

  get(id: number, collections = []): Promise<GeometricPlanRequestModel> {
    return this.restangular
      .one('geometric-plan-requests', id)
      .get({ loadCollections: collections })
      .toPromise();
  }

  update(
      geometricPlanRequest: GeometricPlanRequestModel,
      notes: NoteModel[] = [],
      gpFiles: UploadFileExtended[] = [],
      gpVfkFiles: UploadFileExtended[] = [],
      gpDocFiles: UploadFileExtended[] = [],
      gpOtherFiles: UploadFileExtended[] = [],
      gpVfkConceptFiles: UploadFileExtended[] = [],
    ): Promise<GeometricPlanRequestModel> {
      const restBaseUrl = this.authService.getActiveApplicationRestUrl();
      let attachmentType = null;

      const sendData: any = {
        files: [],
        filename: [],
        geometricPlanRequest: geometricPlanRequest,
        notes: notes,
        attachmentType: [],
        baseName: [],
      };

      attachmentType = AttachmentTypeModel.createById('GEOMETRIC_PLAN').id;
      gpFiles.forEach(file => {
        sendData.files.push(file.fileEntry);
        sendData.filename.push(file.fileEntry.name);
        sendData.attachmentType.push(attachmentType);
        sendData.baseName.push(FileUtils.getFileTitle(file.fileEntry));
      });

      attachmentType = AttachmentTypeModel.createById('GEOMETRIC_PLAN_VFK').id;
      gpVfkFiles.forEach(file => {
        sendData.files.push(file.fileEntry);
        sendData.filename.push(file.fileEntry.name);
        sendData.attachmentType.push(attachmentType);
        sendData.baseName.push(FileUtils.getFileTitle(file.fileEntry));
      });

      attachmentType = AttachmentTypeModel.createById('GEOMETRIC_PLAN_DOCUMENTATION').id;
      gpDocFiles.forEach(file => {
        sendData.files.push(file.fileEntry);
        sendData.filename.push(file.fileEntry.name);
        sendData.attachmentType.push(attachmentType);
        sendData.baseName.push(FileUtils.getFileTitle(file.fileEntry));
      });

      attachmentType = AttachmentTypeModel.createById('GEOMETRIC_PLAN_PROPOSAL').id;
      gpOtherFiles.forEach(file => {
        sendData.files.push(file.fileEntry);
        sendData.filename.push(file.fileEntry.name);
        sendData.attachmentType.push(attachmentType);
        sendData.baseName.push(FileUtils.getFileTitle(file.fileEntry));
      });

      attachmentType = AttachmentTypeModel.createById('GEOMETRIC_PLAN_VFK_CONCEPT').id;
      gpVfkConceptFiles.forEach(file => {
        sendData.files.push(file.fileEntry);
        sendData.filename.push(file.fileEntry.name);
        sendData.attachmentType.push(attachmentType);
        sendData.baseName.push(FileUtils.getFileTitle(file.fileEntry));
      });

      return this.uploadService.upload({
        url: restBaseUrl + '/geometric-plan-requests/' + geometricPlanRequest.id + '?loadCollections=&loadCollections=areas&loadCollections=constructionObjects&loadCollections=parcels&loadCollections=folder',
        formData: FileUtils.objectToJsonMultipartFormData(sendData),
        headers: { Authorization: this.authService.getToken() }
      });
    }

  createNotes(geometricPlanRequest: GeometricPlanRequestModel, notes: NoteModel[] = []): Promise<any> {
    const promises = notes.map(note => {
      return this.restangular
        .all('notes/create')
        .customPOST({ ...note, geometricPlanRequest: geometricPlanRequest })
        .toPromise();
    });
    return Promise.all(promises);
  }

  async createDocuments(geometricPlan: GeometricPlanRequestModel, files: UploadFileExtended[], type: string): Promise<any> {
    const restBaseUrl = this.authService.getActiveApplicationRestUrl();
    const attachmentType = AttachmentTypeModel.createById(type).id;


    if (this.APP_BRAND.NAME === 'RSD') {
      for (const file of files) {
        const sendData = {
          defaultDocument: true,
          file: file.fileEntry,
          filename: file.fileEntry.name,
          geometricPlanRequestId: geometricPlan.id,
          attachmentType: attachmentType
        };

        await this.uploadService.upload({
          url: restBaseUrl + '/documents/create',
          data: sendData,
          headers: { Authorization: this.authService.getToken() }
        }).error(e => this.errorHandlerService.get(e));
      }
    } else {
      const deletePromises = [];

      const uploadPromises = files.map(file => {
        const sendData = {
          file: file.fileEntry,
          filename: file.fileEntry.name,
          geometricPlanRequestId: geometricPlan.id,
          attachmentType: attachmentType
        };

        if (files.length && ['GEOMETRIC_PLAN', 'GEOMETRIC_PLAN_VFK', 'GEOMETRIC_PLAN_DOCUMENTATION'].includes(type) && geometricPlan.id) {
          const promise = this.restangular
            .all('attachments')
            .customPOST({ filter: { filters: { geometricPlanRequestId: geometricPlan.id, cancelled: false, attachmentType: type }}})
            .toPromise()
            .then((data => {
              data.forEach((d) => {
                this.restangular.one('attachments', d.id).remove();
              });
            }));

          deletePromises.push(promise);
        }

        return this.uploadService.upload({
          url: restBaseUrl + '/attachments',
          data: sendData,
          headers: { Authorization: this.authService.getToken() }
        });
      });

      return Promise.all(deletePromises.concat(uploadPromises));
    }
  }

  getEnumLabel(type: 'type' | 'status' | 'approve', value: string): string {
    let src;

    switch (type) {
      case 'type':
        src = geometricPlanRequestTypeOptions;
        break;
      case 'status':
        src = geometricPlanRequestStatusOptions;
        break;
      case 'approve':
        src = approveOptions;
        break;
    }
    return src.find(option => option.id === value).name;
  }

  getStatusEnumAction(value: string): string {
    const option = geometricPlanRequestStatusOptions.find(option => option.id === value);
    return (option.action ? option.action : option.name);
  }

  isStatusDialog(status: string): boolean {
    return geometricPlanRequestStatusOptions.find(option => option.id === status).dialog;
  }
}
